import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['exports-notifications-and-weekly-accountability'];

export default function ExportsNotificationsAccountabilityPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['build-a-shared-plan-with-your-partner', 'how-we-turn-transaction-cleanup-into-better-planning']}
    >
      <QuickVerdict
        title="Quick take"
        items={[
          'A financial product becomes more useful when it stays in the household’s rhythm after onboarding.',
          'Exports, reminders, and recurring check-ins help keep the plan alive between major decisions.',
          'Per Diem is strongest when the household can revisit the plan lightly and consistently, not just during big annual resets.',
        ]}
      />

      <ArticleSection title="The real risk after onboarding is drift">
        <ArticleParagraph>
          Most financial tools can create a burst of motivation at setup time. The harder challenge
          is helping the household keep the system in use once the novelty fades and life gets busy
          again.
        </ArticleParagraph>
        <ArticleParagraph>
          That is why a good planning product needs more than a strong dashboard. It needs lightweight
          ways to bring the household back into the plan.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileHome}
        alt="Per Diem mobile home screen"
        caption="The dashboard matters most when the household actually returns to it often enough for the guidance to influence behavior."
        variant="phone"
      />

      <ArticleSection title="Why supporting systems matter">
        <ArticleBullets
          items={[
            'Exports help users extend the plan into the tools and workflows they already trust.',
            'Notifications create gentle reminders to review progress and catch drift early.',
            'Recurring check-ins make the product part of a routine instead of a once-a-quarter event.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="Weekly accountability is often enough">
        <ArticleParagraph>
          Most households do not need constant alerts. They need a steady rhythm that makes it easy
          to check in, notice what changed, and decide whether anything needs attention. That is the
          level of accountability that keeps a plan useful without making it oppressive.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileLedger}
        alt="Per Diem mobile transaction ledger"
        caption="Lightweight check-ins work because the household can quickly see what changed instead of rebuilding context every time."
        variant="phone"
      />

      <ArticleSection title="The best plan is the one that stays active">
        <ArticleParagraph>
          Exports, notifications, and review loops are supporting features, but they matter because
          they keep the core planning system alive. A plan does not help much if the household only
          touches it when something has already gone off course.
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
